import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getMessaging } from "firebase/messaging";
import { initializeAppCheck, ReCaptchaEnterpriseProvider, getToken } from "firebase/app-check";

// Your web app's Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Variables to export
let auth, db, storage, messaging, app;
let initialized = false;
let appCheckEnabled = false;

// Function to wait for reCAPTCHA to be loaded
const waitForReCaptcha = (maxWaitTime = 10000) => {
  return new Promise((resolve, reject) => {
    // If grecaptcha is already available, resolve immediately
    if (window.grecaptcha) {
      console.log('reCAPTCHA already loaded');
      return resolve(window.grecaptcha);
    }

    console.log('Waiting for reCAPTCHA to load...');
    
    // Set a timeout for maximum wait time
    const timeoutId = setTimeout(() => {
      reject(new Error('reCAPTCHA failed to load within timeout period'));
    }, maxWaitTime);
    
    // Check every 100ms if grecaptcha is available
    const intervalId = setInterval(() => {
      if (window.grecaptcha) {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
        console.log('reCAPTCHA loaded successfully');
        resolve(window.grecaptcha);
      }
    }, 100);
  });
};

const initializeFirebase = async () => {
  try {
    app = initializeApp(firebaseConfig);

    // Enhanced diagnostics for ReCAPTCHA site key
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
    console.log('Using reCAPTCHA site key:', siteKey ? siteKey.substring(0, 4) + '...' + siteKey.substring(siteKey.length - 4) : '[MISSING]');
    console.log('Current origin:', window.location.origin);
    
    // Check if we're accessing via IP address (mobile testing scenario)
    const isIPAccess = window.location.hostname.match(/^\d+\.\d+\.\d+\.\d+$/);
    
    if (isIPAccess) {
      console.warn('⚠️ Accessing via IP address - skipping App Check and Messaging for mobile testing');
      
      // For testing purposes, let's trigger the error handler when accessing via IP
      console.log('🧪 Testing: Setting Firebase error for IP access');
      window.FIREBASE_INIT_ERROR = {
        message: 'App accessed via IP address',
        originalError: new Error('IP access detected'),
        isNetworkIssue: true,
        suggestion: 'For full functionality, try accessing via localhost instead of IP address.'
      };
      
      appCheckEnabled = false;
    } else {
      // Wait for reCAPTCHA to be loaded before initializing App Check
      try {
        await waitForReCaptcha();
        
        const appCheck = initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(siteKey),
          isTokenAutoRefreshEnabled: true,
        });

        // Get initial App Check token
        const token = await getToken(appCheck, /* forceRefresh */ true);
        console.log('Initial App Check token acquired:', !!token);
        appCheckEnabled = true;
      } catch (recaptchaError) {
        console.warn('App Check initialization failed, continuing without it:', recaptchaError.message);
        appCheckEnabled = false;
        // Don't throw here - continue with basic Firebase functionality
      }
    }

    // Initialize core Firebase services
    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
    
    // Try to initialize messaging, but don't fail if it doesn't work
    try {
      if (!isIPAccess) {
        messaging = getMessaging(app);
        console.log('Firebase Messaging initialized successfully');
      } else {
        console.log('Skipping Firebase Messaging for IP access');
      }
    } catch (messagingError) {
      console.warn('Firebase Messaging initialization failed, continuing without it:', messagingError.message);
      messaging = null;
    }
    
    initialized = true;
    console.log('✅ Firebase initialization completed successfully');
  } catch (error) {
    console.error('❌ Critical Firebase initialization error:', error);
    
    // Set a global flag that the app can check
    window.FIREBASE_INIT_ERROR = {
      message: 'Unable to connect to our services',
      originalError: error,
      isNetworkIssue: error.message?.includes('addEventListener') || error.message?.includes('reCAPTCHA'),
      suggestion: 'Please check your internet connection and try again.'
    };
    
    throw error;
  }
};

// Create a promise that resolves when Firebase is initialized
export const firebaseInitialized = initializeFirebase().catch(error => {
  console.error('Failed to initialize Firebase:', error);
  // Don't re-throw the error, just log it and continue
  // The app will check window.FIREBASE_INIT_ERROR if needed
  return false;
});

// Exports
export { auth, db, storage, messaging, initialized, appCheckEnabled };
export default app;
